import React, { useEffect, useState } from "react";
import { HistoryItem } from "../../types";
import { getHistory } from "../history";
import styles from "./RecentScores.module.css";

// Colour a score by ASLAN band (mirrors the BigScoreCard palette).
function bandColor(score: number | null | undefined): string {
  if (score == null) return "var(--color-text-faint)";
  if (score >= 85) return "var(--color-success)";
  if (score >= 70) return "var(--color-accent)";
  if (score >= 50) return "var(--color-warning)";
  return "var(--color-brand-red)";
}

// The backend stores UTC but (via Mongo) can emit a timestamp with no timezone
// designator; `new Date()` would then read it as local time and show the wrong
// "x ago". Treat a tz-less string as UTC.
function parseTimestamp(iso: string): number {
  const hasTz = /[zZ]$|[+-]\d\d:?\d\d$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`).getTime();
}

function relativeTime(iso: string): string {
  try {
    const then = parseTimestamp(iso);
    const diff = Date.now() - then;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(then).toLocaleDateString();
  } catch {
    return "";
  }
}

const TYPE_LABEL: Record<HistoryItem["type"], string> = {
  analyse: "Analysed",
  generate: "Written",
  quick_score: "Quick score",
};

// Only analyse / generate runs persist a full result screen to re-open.
// quick_score keeps a score but no breakdown, so its rows stay non-interactive.
const OPENABLE: Record<HistoryItem["type"], boolean> = {
  analyse: true,
  generate: true,
  quick_score: false,
};

// Show only the most recent few sessions in the home list.
const MAX_VISIBLE = 3;

interface RecentScoresProps {
  // Open a stored run's result screen. Returns a promise so the row can show a
  // loading state while the detail is fetched.
  onOpen?: (item: HistoryItem) => Promise<void> | void;
}

export default function RecentScores({ onOpen }: RecentScoresProps) {
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  const [errored, setErrored] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openErrorId, setOpenErrorId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getHistory(MAX_VISIBLE)
      .then((page) => {
        if (!cancelled) setItems(page.items);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entirely on error (e.g. dev/mock mode without a backend session).
  if (errored) return null;
  if (items === null) {
    return (
      <div className={styles.wrap}>
        <div className={styles.header}>Recent sessions</div>
        <div className={styles.empty}>Loading…</div>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.header}>Recent sessions</div>
        <div className={styles.empty}>No analyses yet — score a draft to start tracking your progress.</div>
      </div>
    );
  }

  const visible = items.slice(0, MAX_VISIBLE);

  const handleOpen = async (it: HistoryItem) => {
    if (!onOpen || openingId) return;
    setOpenErrorId(null);
    setOpeningId(it.id);
    try {
      await onOpen(it);
    } catch {
      setOpenErrorId(it.id);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.headerRow}
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        title={collapsed ? "Show recent sessions" : "Hide recent sessions"}
      >
        <span className={styles.header}>Recent sessions</span>
        <span className={`${styles.toggle} ${collapsed ? styles.toggleCollapsed : ""}`} aria-hidden="true">
          ⌄
        </span>
      </button>
      {!collapsed && (
      <ul className={styles.list}>
        {visible.map((it) => {
          const score = it.originalScore ?? null;
          const improved =
            it.type === "analyse" && it.scoreAfter != null && score != null && it.scoreAfter > score;
          const clickable = !!onOpen && OPENABLE[it.type];
          const opening = openingId === it.id;

          const inner = (
            <>
              <span className={styles.badge} style={{ background: bandColor(score) }}>
                {score ?? "—"}
              </span>
              <span className={styles.meta}>
                <span className={styles.subject} title={it.subject}>
                  {it.subject || "(no subject)"}
                </span>
                <span className={styles.sub}>
                  {openErrorId === it.id
                    ? "Couldn't open — tap to retry"
                    : (
                      <>
                        {TYPE_LABEL[it.type]} · {relativeTime(it.createdAt)}
                        {improved && (
                          <span className={styles.delta}> · ↑ {it.scoreAfter} with ASLAN+</span>
                        )}
                      </>
                    )}
                </span>
              </span>
              {clickable && (
                <span className={styles.chevron} aria-hidden="true">
                  {opening ? "…" : "›"}
                </span>
              )}
            </>
          );

          return (
            <li key={it.id} className={styles.rowItem}>
              {clickable ? (
                <button
                  type="button"
                  className={`${styles.row} ${styles.rowClickable}`}
                  onClick={() => handleOpen(it)}
                  disabled={opening}
                  title="Open this result"
                >
                  {inner}
                </button>
              ) : (
                <div className={styles.row}>{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}
